import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { toast } from 'sonner';

interface Department {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  positionCount: number;
  employeeCount: number;
}

interface DepartmentDialogProps {
  department: Department | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string | null, data: any) => Promise<void>;
}

export const DepartmentDialog: React.FC<DepartmentDialogProps> = ({
  department,
  open,
  onClose,
  onSave,
}) => {
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    if (department) {
      setFormData({
        name: department.name,
        description: department.description || '',
        isActive: department.isActive,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });
    }
    setFormError('');
  }, [department, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Nazwa działu jest wymagana');
      return;
    }

    try {
      setSaving(true);

      const saveData: any = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      };

      if (department) {
        saveData.isActive = formData.isActive;
      }

      await onSave(department?.id || null, saveData);

      toast.success(department
        ? `Dział "${formData.name}" został zaktualizowany`
        : `Dział "${formData.name}" został dodany`
      );

      onClose();
    } catch (error: any) {
      const errorMessage = error.message || 'Wystąpił błąd podczas zapisywania działu';
      setFormError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="sticky top-0 z-10 bg-background px-6 pt-6 pb-4 border-b">
          <DialogTitle>
            {department ? `Edytuj dział: ${department.name}` : 'Dodaj nowy dział'}
          </DialogTitle>
          <DialogDescription>
            {department ? 'Zaktualizuj dane działu' : 'Wypełnij formularz aby dodać dział do systemu'}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4">
          <form id="department-form" onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="text-red-500 text-sm bg-red-50 p-3 rounded-md border border-red-200">
                {formError}
              </div>
            )}

            <div>
              <Label htmlFor="name">Nazwa działu <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="np. Produkcja"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Opis działu</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Opisz zakres działalności działu..."
                rows={3}
              />
            </div>

            {department && (
              <div>
                <Label htmlFor="isActive">Status</Label>
                <select
                  id="isActive"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={formData.isActive.toString()}
                  onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
                >
                  <option value="true">Aktywny</option>
                  <option value="false">Nieaktywny</option>
                </select>
              </div>
            )}
          </form>
        </div>

        <DialogFooter className="sticky bottom-0 z-10 bg-background px-6 pb-6 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Anuluj
          </Button>
          <Button type="submit" form="department-form" disabled={saving}>
            {saving ? 'Zapisywanie...' : (department ? 'Zapisz zmiany' : 'Dodaj dział')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
